import { useCallback, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type {
  BookingEventDetail,
  BookingEventRegistrationFormValues,
  ClientOption,
  MutationResponse,
} from '@/types';
import type { TFunction } from 'i18next';

import { Form } from '@/components/Form';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { useFormPage } from '@/hooks';

import { notifyBookingSuccess } from '@/helpers';

import { createBookingEventRegistrationSchema } from '@/types/schemas';

import { BookingDialogBody } from '../BookingDialogBody';
import { BookingDialogFooter } from '../BookingDialogFooter';
import { ClientAutoComplete } from '../ClientAutoComplete';
import { ServiceCustomFields } from '../ServiceCustomFields';

import { createEventRegistrationPayload, registeredIn } from './utils';

type RegistrationKind = 'client' | 'guest';

type AddRegistrationFormProps = {
  event: BookingEventDetail;
  eventsUrl: string;
  clientsSearchUrl: string;
  waveId: number | null;
  onClose: () => void;
  onSuccess: () => void;
};

// No coverage picker: money for an event registration is a separate reception sale.
export const AddRegistrationForm = (props: AddRegistrationFormProps) => {
  const {
    event,
    eventsUrl,
    clientsSearchUrl,
    waveId,
    onClose,
    onSuccess: propOnSuccess,
  } = props;
  const { t } = useTranslation();

  const [kind, setKind] = useState<RegistrationKind>('client');
  const [client, setClient] = useState<ClientOption | null>(null);

  const customFields = useMemo(
    () => registeredIn(event, waveId)?.service.service_custom_fields ?? [],
    [event, waveId],
  );

  const requiredCustomFieldIds = useMemo(
    () =>
      customFields.filter((field) => field.required).map((field) => field.id),
    [customFields],
  );

  const schema = useCallback(
    (translate: TFunction) =>
      createBookingEventRegistrationSchema(translate, requiredCustomFieldIds),
    [requiredCustomFieldIds],
  );

  const transform = useCallback(
    (values: BookingEventRegistrationFormValues) =>
      createEventRegistrationPayload(values, waveId),
    [waveId],
  );

  const onSuccess = useCallback(
    (response: MutationResponse) => {
      notifyBookingSuccess(response);
      propOnSuccess();
      onClose();
    },
    [onClose, propOnSuccess],
  );

  const defaultValues = useMemo<BookingEventRegistrationFormValues>(
    () => ({
      client_id: '',
      guest_info: '',
      note: '',
      staff_comment: '',
      custom_field_values: {},
    }),
    [],
  );

  const { form, onSubmit, isPending } =
    useFormPage<BookingEventRegistrationFormValues>({
      schema,
      defaultValues,
      transform,
      api_url: `${eventsUrl}/${event.id}/registrations`,
      redirectUrl: '',
      onSuccess,
    });

  const customFieldValues = useWatch({
    control: form.control,
    name: 'custom_field_values',
  });

  const onKindChange = useCallback(
    (value: string) => {
      setKind(value as RegistrationKind);
      setClient(null);
      form.setValue('client_id', '');
      form.setValue('guest_info', '');
    },
    [form],
  );

  const onClientChange = useCallback(
    (value: ClientOption | null) => {
      setClient(value);
      form.setValue('client_id', value ? String(value.id) : '', {
        shouldValidate: form.formState.isSubmitted,
      });
    },
    [form],
  );

  const onCustomFieldChange = useCallback(
    (fieldId: number, value: string) => {
      form.setValue(
        'custom_field_values',
        { ...form.getValues('custom_field_values'), [fieldId]: value },
        { shouldValidate: form.formState.isSubmitted },
      );
    },
    [form],
  );

  const customFieldErrors = useMemo(() => {
    const nested = form.formState.errors.custom_field_values as
      Record<string, { message?: string } | undefined> | undefined;

    return Object.entries(nested ?? {}).reduce<Record<string, string>>(
      (result, [fieldId, error]) =>
        error?.message ? { ...result, [fieldId]: error.message } : result,
      {},
    );
  }, [form.formState.errors.custom_field_values]);

  return (
    <>
      <BookingDialogBody>
        <p className="text-sm font-medium">{t('Add registration')}</p>

        <Tabs
          value={kind}
          onValueChange={onKindChange}
        >
          <TabsList className="mb-0 w-full">
            <TabsTrigger value="client">{t('Existing client')}</TabsTrigger>
            <TabsTrigger value="guest">{t('Guest')}</TabsTrigger>
          </TabsList>
        </Tabs>

        {kind === 'client' && (
          <>
            <ClientAutoComplete
              fetchUrl={clientsSearchUrl}
              queryKey={['booking_event_registration_client']}
              label={t('Client')}
              value={client}
              onChange={onClientChange}
            />
            {!!form.formState.errors.guest_info && !client && (
              <p className="text-destructive -mt-2 text-sm">
                {form.formState.errors.guest_info.message}
              </p>
            )}
          </>
        )}

        <Form
          form={form}
          onSubmit={onSubmit}
        >
          {kind === 'guest' && (
            <Form.Input
              name="guest_info"
              label={t('Guest name / phone')}
              isRequired
            />
          )}

          <ServiceCustomFields
            fields={customFields}
            values={customFieldValues}
            errors={customFieldErrors}
            onChange={onCustomFieldChange}
          />

          <Form.Textarea
            name="note"
            label={t('Note')}
          />
          <Form.Textarea
            name="staff_comment"
            label={t('Specialist comment')}
          />
        </Form>
      </BookingDialogBody>

      <BookingDialogFooter
        submitLabel={t('Add registration')}
        isPending={isPending}
        onClose={onClose}
        onSubmit={onSubmit}
      />
    </>
  );
};
